import React, { useCallback, useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Alert from "@mui/material/Alert";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import LinearProgress from "@mui/material/LinearProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import ReplayIcon from "@mui/icons-material/Replay";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { CONNECTION_STATUSES } from "../constants";
import {
  MONITOR_INTERVALS,
  exportVerificationCsv,
  exportVerificationHtml,
  exportVerificationJson,
  exportVerificationPdf,
  formatVerificationReport,
  listConnectionVerificationHistory,
} from "../services/connectionVerifyService";
import { listFirestoreCollections } from "../../../services/adminApi";

const MONITOR_STORAGE_KEY = "studio_connection_monitor_interval";

const TAB_LABELS = [
  "Summary",
  "Diagnostics",
  "Permissions",
  "Performance",
  "Collections",
  "Indexes",
  "Security",
  "Configuration",
  "Raw JSON",
  "History",
];

function statusColor(status) {
  if (status === "success") return "success";
  if (status === "not_configured" || status === "skipped" || status === "not_enabled") return "default";
  if (status === "not_signed_in") return "info";
  if (status === "read_only" || status === "permission_denied" || status === "warning") return "warning";
  if (status === "failed") return "error";
  return "default";
}

function statusLabel(status) {
  if (status === "not_configured") return "Not Configured";
  if (status === "not_signed_in") return "Not signed in";
  if (status === "read_only") return "Read-only access";
  if (status === "not_enabled") return "Not Configured";
  return status;
}

function statusIcon(status) {
  if (status === "success") return "✔";
  if (status === "not_configured" || status === "skipped" || status === "not_enabled") return "○";
  if (status === "not_signed_in") return "○";
  if (status === "read_only" || status === "permission_denied") return "⚠";
  if (status === "failed") return "✘";
  return "⚠";
}

function overallMeta(overall) {
  return CONNECTION_STATUSES.find((s) => s.value === overall) || { label: overall, color: "default" };
}

function gradeColor(grade) {
  if (grade === "green") return "success";
  if (grade === "yellow") return "warning";
  return "error";
}

function downloadText(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function SeverityChip({ severity }) {
  const color = severity === "error" ? "error" : severity === "warning" ? "warning" : "default";
  return <Chip size="small" label={severity} color={color} variant="outlined" />;
}

export default function ConnectionVerifyDialog({
  open,
  connection,
  result,
  loading,
  onClose,
  onRerun,
}) {
  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [monitorMs, setMonitorMs] = useState(() => {
    try {
      return Number(localStorage.getItem(MONITOR_STORAGE_KEY) || 0);
    } catch {
      return 0;
    }
  });

  const [connectionHealth, setConnectionHealth] = useState({
    loading: false,
    error: null,
    data: null,
  });

  const overall = result ? overallMeta(result.overall) : null;
  const baseName = connection?.name?.replace(/\s+/g, "-") || "connection";

  const loadHistory = useCallback(async () => {
    if (!connection?.id) return;
    setHistoryLoading(true);
    try {
      const rows = await listConnectionVerificationHistory(connection.id, 50);
      setHistory(rows);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [connection?.id]);

  useEffect(() => {
    if (open && tab === 9 && connection?.id) loadHistory();
  }, [open, tab, connection?.id, loadHistory]);

  useEffect(() => {
    if (!open || !connection?.id) return;
    let cancelled = false;
    setConnectionHealth({ loading: true, error: null, data: null });
    (async () => {
      try {
        const resp = await listFirestoreCollections({
          connectionId: connection.id,
          runtimeProjectId: connection.firebaseProjectId || "",
        });
        const data = resp?.data ?? resp;
        if (cancelled) return;
        setConnectionHealth({ loading: false, error: null, data });
      } catch (err) {
        if (cancelled) return;
        setConnectionHealth({ loading: false, error: err, data: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, connection?.id, connection?.firebaseProjectId]);

  useEffect(() => {
    if (result && connection?.id) loadHistory();
  }, [result?.checkedAt, connection?.id, loadHistory, result]);

  useEffect(() => {
    if (!open || !monitorMs || !onRerun || !connection) return undefined;
    const id = setInterval(() => onRerun(), monitorMs);
    return () => clearInterval(id);
  }, [open, monitorMs, onRerun, connection]);

  const handleMonitorChange = (ms) => {
    setMonitorMs(ms);
    try {
      localStorage.setItem(MONITOR_STORAGE_KEY, String(ms));
    } catch { /* ignore */ }
  };

  const chartData = useMemo(
    () =>
      [...history]
        .reverse()
        .map((h) => ({
          label: h.verifiedAt ? new Date(h.verifiedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "",
          score: h.healthScore ?? 0,
          latency: h.latency?.total ?? h.report?.responseTimeMs ?? 0,
        })),
    [history]
  );

  const handleCopy = async () => {
    const text = formatVerificationReport(result);
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportJson = () => {
    downloadText(`${baseName}-health-check.json`, exportVerificationJson(result), "application/json");
  };

  const handleExportCsv = () => {
    downloadText(`${baseName}-health-check.csv`, exportVerificationCsv(result), "text/csv");
  };

  const handleExportHtml = () => {
    downloadText(`${baseName}-health-check.html`, exportVerificationHtml(result), "text/html");
  };

  const handleExportPdf = async () => {
    await exportVerificationPdf(result, `${baseName}-health-check.pdf`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="h6" fontWeight={700}>
            Firebase Health Check Suite
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {result && (
              <Chip size="small" label={`SDK ${result.advanced?.sdkVersion || "12.x"}`} variant="outlined" />
            )}
            {onRerun && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="monitor-label">
                  <MonitorHeartIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
                  Auto
                </InputLabel>
                <Select
                  labelId="monitor-label"
                  label="Auto"
                  value={monitorMs}
                  onChange={(e) => handleMonitorChange(Number(e.target.value))}
                >
                  {MONITOR_INTERVALS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {connection && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {connection.name} · <code>{connection.firebaseProjectId}</code>
          </Typography>
        )}

        {connection && (
          <Box sx={{ mb: 2, p: 1.5, border: "1px solid rgba(0,0,0,0.08)", borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
              Connection Health
            </Typography>
            {connectionHealth.loading ? (
              <Typography variant="body2" color="text.secondary">
                Checking Admin SDK access and collection discovery…
              </Typography>
            ) : connectionHealth.error ? (
              <Alert severity="error">
                <Typography variant="body2">
                  Admin SDK status: <b>failed</b>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {connectionHealth.error?.message || "Permission denied or service error"}
                </Typography>
                {connectionHealth.error?.details?.requiredRoles?.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Required IAM roles (target project):
                    </Typography>
                    {connectionHealth.error.details.requiredRoles.map((r, i) => (
                      <Typography key={i} variant="caption" display="block">
                        • {r}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            ) : connectionHealth.data ? (
              <Box>
                {connectionHealth.data.crossProject && connectionHealth.data.credentialMode !== "connection_service_account" && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Cross-project discovery may fail because this connection has no Service Account configured.
                  </Alert>
                )}
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    Runtime Project:{" "}
                    <code>{connection.firebaseProjectId}</code>
                  </Typography>
                  <Typography variant="body2">
                    Admin Project: <code>{connectionHealth.data.adminProjectId || "—"}</code>
                  </Typography>
                  <Typography variant="body2">
                    Admin App Name: <code>{connectionHealth.data.adminAppName || "—"}</code>
                  </Typography>
                  <Typography variant="body2">
                    Service Account Email:{" "}
                    <code>{connectionHealth.data.serviceAccountEmail || "—"}</code>
                  </Typography>
                  <Typography variant="body2">
                    Admin SDK Status: <b>OK</b>
                  </Typography>
                  <Typography variant="body2">
                    Collection Discovery Status: <b>OK</b> ({connectionHealth.data.count ?? 0})
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Admin SDK status: not started
              </Typography>
            )}
          </Box>
        )}

        {loading && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 4 }}>
            <CircularProgress size={36} />
            <Typography variant="body2">Running enterprise Firebase health diagnostics…</Typography>
            <Typography variant="caption" color="text.secondary">
              SDK-only · isolated app · permissions · collections · security audit
            </Typography>
          </Box>
        )}

        {!loading && result && (
          <Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
                  <Chip label={overall?.label || result.overall} color={overall?.color || "default"} />
                  <Chip
                    label={`Health Score ${result.healthScore}%`}
                    color={gradeColor(result.healthGrade)}
                    variant="outlined"
                  />
                  {monitorMs > 0 && (
                    <Chip size="small" icon={<MonitorHeartIcon />} label="Auto-monitor on" color="info" variant="outlined" />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Total {result.responseTimeMs} ms · {result.advanced?.browser} · {result.advanced?.os} · Engine: {result.engine}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={result.healthScore}
                  color={gradeColor(result.healthGrade)}
                  sx={{ mt: 1, height: 6, borderRadius: 1 }}
                />
              </Box>
            </Stack>

            {result.warnings?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {result.warnings.map((w, i) => (
                  <Typography key={i} variant="body2">{w}</Typography>
                ))}
              </Alert>
            )}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
              {TAB_LABELS.map((label) => (
                <Tab key={label} label={label} />
              ))}
            </Tabs>

            {/* Summary */}
            <TabPanel value={tab} index={0}>
              <List dense disablePadding>
                {(result.checks || []).map((check, i) => (
                  <ListItem key={`${check.service}-${i}`} disableGutters sx={{ alignItems: "flex-start" }}>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="body2" fontWeight={600} textTransform="capitalize" sx={{ minWidth: 140 }}>
                            {statusIcon(check.status)} {check.service}
                          </Typography>
                          <Chip size="small" label={statusLabel(check.status)} color={statusColor(check.status)} />
                          {check.latencyMs > 0 && (
                            <Typography variant="caption" color="text.secondary">{check.latencyMs} ms</Typography>
                          )}
                        </Stack>
                      }
                      secondary={check.message}
                    />
                  </ListItem>
                ))}
              </List>
            </TabPanel>

            {/* Diagnostics */}
            <TabPanel value={tab} index={1}>
              {result.diagnostics?.length ? (
                <List dense>
                  {result.diagnostics.map((d, i) => (
                    <ListItem key={i} divider>
                      <ListItemText
                        primary={d.message}
                        secondary={`${d.category} · ${d.severity}${d.field ? ` · ${d.field}` : ""}`}
                      />
                      <SeverityChip severity={d.severity} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">No diagnostic issues detected.</Typography>
              )}
            </TabPanel>

            {/* Permissions */}
            <TabPanel value={tab} index={2}>
              {result.permissions ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Test</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Message</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(result.permissions).map(([key, p]) => (
                      <TableRow key={key}>
                        <TableCell sx={{ textTransform: "capitalize" }}>{key}</TableCell>
                        <TableCell>
                          <Chip size="small" label={statusLabel(p.status)} color={statusColor(p.status)} />
                        </TableCell>
                        <TableCell>{p.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary">Permission tests not run (secrets masked or offline).</Typography>
              )}
            </TabPanel>

            {/* Performance */}
            <TabPanel value={tab} index={3}>
              <Stack spacing={1}>
                {["init", "authentication", "firestore", "storage", "functions", "hosting", "total"].map((key) => {
                  const ms = result.performance?.[key] ?? result.latency?.[key] ?? 0;
                  return (
                    <Stack key={key} direction="row" justifyContent="space-between">
                      <Typography variant="body2" textTransform="capitalize">{key}</Typography>
                      <Typography variant="body2" fontWeight={600}>{ms} ms</Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </TabPanel>

            {/* Collections */}
            <TabPanel value={tab} index={4}>
              {result.collections ? (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={3} flexWrap="wrap">
                    <Box>
                      <Typography variant="caption" color="text.secondary">Total Collections</Typography>
                      <Typography variant="h6">{result.collections.totalCollections ?? "—"}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Estimated Documents</Typography>
                      <Typography variant="h6">{result.collections.estimatedDocuments ?? "—"}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Avg Document Size</Typography>
                      <Typography variant="h6">
                        {result.collections.averageDocumentSize != null
                          ? `${result.collections.averageDocumentSize} B`
                          : "—"}
                      </Typography>
                    </Box>
                  </Stack>
                  {result.collections.largestCollection && (
                    <Alert severity="info" variant="outlined">
                      Largest collection: <strong>{result.collections.largestCollection.name}</strong>
                      {" "}({result.collections.largestCollection.count} docs sampled)
                    </Alert>
                  )}
                  {result.collections.sampleDocuments?.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Sample Documents</Typography>
                      <List dense>
                        {result.collections.sampleDocuments.map((s, i) => (
                          <ListItem key={i} disableGutters>
                            <ListItemText
                              primary={`${s.collection} / ${s.id}`}
                              secondary={s.fields ? `Fields: ${s.fields.join(", ")}` : undefined}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Stack>
              ) : (
                <Typography color="text.secondary">Collection analysis not available.</Typography>
              )}
            </TabPanel>

            {/* Indexes */}
            <TabPanel value={tab} index={5}>
              {result.indexes ? (
                <Stack spacing={2}>
                  <Typography variant="body2">
                    Missing composite indexes: {result.indexes.missingCount ?? 0}
                  </Typography>
                  {result.indexes.required?.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Collection</TableCell>
                          <TableCell>Fields</TableCell>
                          <TableCell>Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.indexes.required.map((idx, i) => (
                          <TableRow key={i}>
                            <TableCell>{idx.collection}</TableCell>
                            <TableCell><code>{idx.fields?.join(", ")}</code></TableCell>
                            <TableCell>{idx.reason}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography color="text.secondary">No missing composite indexes detected.</Typography>
                  )}
                </Stack>
              ) : (
                <Typography color="text.secondary">Index analysis not available.</Typography>
              )}
            </TabPanel>

            {/* Security */}
            <TabPanel value={tab} index={6}>
              {result.security?.length ? (
                <List dense>
                  {result.security.map((f, i) => (
                    <ListItem key={i} divider>
                      <ListItemText primary={f.message} secondary={f.type} />
                      <SeverityChip severity={f.severity === "info" ? "default" : f.severity} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">No security findings.</Typography>
              )}
            </TabPanel>

            {/* Configuration */}
            <TabPanel value={tab} index={7}>
              <Stack spacing={2}>
                {result.advanced?.configFields && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Field</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(result.advanced.configFields).map(([key, f]) => (
                        <TableRow key={key}>
                          <TableCell>{f.label || key}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={f.present ? "Present" : "Missing"}
                              color={f.present ? "success" : "error"}
                            />
                          </TableCell>
                          <TableCell><code>{f.value || "—"}</code></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                <Typography variant="subtitle2">Runtime</Typography>
                <Stack spacing={0.5}>
                  {result.advanced && Object.entries(result.advanced)
                    .filter(([k]) => k !== "configFields")
                    .map(([k, v]) => (
                      <Stack key={k} direction="row" justifyContent="space-between" gap={2}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>
                          {k.replace(/([A-Z])/g, " $1")}
                        </Typography>
                        <Typography variant="caption" fontFamily="monospace">{String(v)}</Typography>
                      </Stack>
                    ))}
                </Stack>
              </Stack>
            </TabPanel>

            {/* Raw JSON */}
            <TabPanel value={tab} index={8}>
              <Typography
                component="pre"
                sx={{ fontSize: 11, bgcolor: "action.hover", p: 2, borderRadius: 1, overflow: "auto", maxHeight: 420 }}
              >
                {exportVerificationJson(result)}
              </Typography>
            </TabPanel>

            {/* History */}
            <TabPanel value={tab} index={9}>
              {historyLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Stack spacing={3}>
                  {chartData.length > 1 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Health Score History</Typography>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Line type="monotone" dataKey="score" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} name="Score" />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                  {history.length ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Verified At</TableCell>
                          <TableCell>Score</TableCell>
                          <TableCell>Latency</TableCell>
                          <TableCell>By</TableCell>
                          <TableCell>Browser</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {history.map((h) => (
                          <TableRow key={h.id}>
                            <TableCell>
                              {h.verifiedAt ? new Date(h.verifiedAt).toLocaleString() : "—"}
                            </TableCell>
                            <TableCell>{h.healthScore ?? "—"}%</TableCell>
                            <TableCell>{h.latency?.total ?? h.report?.responseTimeMs ?? "—"} ms</TableCell>
                            <TableCell>{h.verifiedBy || "—"}</TableCell>
                            <TableCell>{h.browser || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography color="text.secondary">No verification history yet.</Typography>
                  )}
                </Stack>
              )}
            </TabPanel>

            {result.checkedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
                Verified at {new Date(result.checkedAt).toLocaleString()}
                {result.advanced?.verifiedBy && result.advanced.verifiedBy !== "—"
                  ? ` by ${result.advanced.verifiedBy}`
                  : ""}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 2, py: 1.5 }}>
        {!loading && result && (
          <>
            <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
              {copied ? "Copied" : "Copy Report"}
            </Button>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleExportJson}>JSON</Button>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleExportCsv}>CSV</Button>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleExportHtml}>HTML</Button>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleExportPdf}>PDF</Button>
            {onRerun && (
              <Button size="small" startIcon={<ReplayIcon />} onClick={onRerun} disabled={loading}>
                Re-run
              </Button>
            )}
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>{loading ? "Cancel" : "Close"}</Button>
      </DialogActions>
    </Dialog>
  );
}
